<template>
  <section :id="sectionId" class="neo-hero">
    <video
      v-if="mdAndUp"
      :src="kompVideo"
      class="hero-bg-video"
      autoplay
      loop
      muted
      playsinline
      preload="none"
      aria-hidden="true"
      tabindex="-1"
    ></video>
    <div class="hero-video-scrim"></div>
    <div class="bg-orb orb1"></div>
    <div class="bg-orb orb2"></div>

    <v-container fluid class="py-16 py-md-24 hero-container">
      <v-row align="center" class="hero-content-row">
        <v-col cols="12" md="6">
          <video
            v-if="!mdAndUp"
            :src="kompMobileVideo"
            class="hero-mobile-video mb-6"
            autoplay
            loop
            muted
            playsinline
            preload="none"
            aria-hidden="true"
            tabindex="-1"
          ></video>

          <div class="eyebrow">{{ t('hero.eyebrow') }}</div>

          <h1 class="hero-product-heading mb-4 reveal" data-reveal="curtain">
            <img
              :src="linqOneLogo"
              :srcset="`${linqOneLogoSmall} 420w, ${linqOneLogoMedium} 840w, ${linqOneLogo} 973w`"
              sizes="(max-width: 960px) 190px, (max-width: 1400px) 31vw, 420px"
              alt="LinqOne"
              class="hero-product-logo"
              width="973"
              height="343"
              fetchpriority="high"
              decoding="async"
            />
          </h1>
          <p class="lead hero-lead mb-6 reveal reveal-delay-06" data-reveal="curtain">
            {{ t('hero.lead') }}
          </p>

          <div class="cta-group reveal reveal-delay-12" data-reveal="curtain">
            <v-btn color="primary" size="large" class="cta cta-primary-lg" @click="emit('nav', 'contact')">
              {{ t('cta.demoNow') }}
            </v-btn>
          </div>

          <div class="badges mt-6 reveal reveal-delay-18" data-reveal="curtain">
            <v-chip class="badge badge-new" size="large" variant="elevated">
              <v-icon start>mdi-shield-check</v-icon> {{ t('hero.badges.dsgvo') }}
            </v-chip>
            <v-chip class="badge badge-new" size="large" variant="elevated">
              <v-icon start>mdi-cloud-outline</v-icon> {{ t('hero.badges.online') }}
            </v-chip>
            <v-chip class="badge badge-new" size="large" variant="elevated">
              <v-icon start>mdi-speedometer</v-icon> {{ t('hero.badges.kpis') }}
            </v-chip>
          </div>
          <div class="hero-trusted mt-6 reveal" data-reveal="soft">
            <div class="hero-trusted-title">{{ t('hero.trustedTitle') }}</div>
            <div class="trust-marquee" :aria-label="locale === 'en' ? 'Selected companies' : 'Auswahl an Unternehmen'">
              <div class="trust-track" :class="{ 'is-paused': motionPaused || !isTrustMarqueeInView }">
                <div v-for="(brand, idx) in trustedBrands" :key="`a-${idx}`" class="trust-item">
                  <div v-if="brand.logo" class="trust-logo-wrap">
                    <img :src="brand.logo" :alt="`${brand.name} Logo`" class="trust-logo" width="136" height="54" loading="lazy" decoding="async" />
                  </div>
                  <div v-else class="trust-placeholder">{{ brand.name.slice(0, 2).toUpperCase() }}</div>
                </div>
                <div v-for="(brand, idx) in trustedBrands" :key="`b-${idx}`" class="trust-item" aria-hidden="true">
                  <div v-if="brand.logo" class="trust-logo-wrap">
                    <img :src="brand.logo" :alt="`${brand.name} Logo`" class="trust-logo" width="136" height="54" loading="lazy" decoding="async" />
                  </div>
                  <div v-else class="trust-placeholder">{{ brand.name.slice(0, 2).toUpperCase() }}</div>
                </div>
              </div>
            </div>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </section>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import linqOneLogo from '../../assets/LinqOne_Logo.webp'
import linqOneLogoSmall from '../../assets/LinqOne_Logo_420.webp'
import linqOneLogoMedium from '../../assets/LinqOne_Logo_840.webp'

type NavAnchor = 'contact'

defineProps<{
  sectionId: string
  mdAndUp: boolean
  kompVideo: string
  kompMobileVideo: string
  motionPaused: boolean
  isTrustMarqueeInView: boolean
  trustedBrands: ReadonlyArray<{ name: string; logo?: string }>
  locale: string
}>()

const emit = defineEmits<{
  nav: [anchor: NavAnchor]
  'toggle-motion': []
}>()

const { t } = useI18n()
</script>

<style scoped>
.neo-hero {
  position: relative;
  overflow: clip;
  padding-block: 0;
  background: #f8fafc;
}

.neo-hero::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: clamp(130px, 16vw, 240px);
  pointer-events: none;
  z-index: 2;
  background: linear-gradient(180deg, transparent 0%, #edf4fa 100%);
}

.hero-bg-video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 68% center;
  pointer-events: none;
  z-index: 0;
}

.hero-video-scrim {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  background: linear-gradient(90deg, transparent 0%, rgba(248, 250, 252, .28) 34%, rgba(248, 250, 252, .16) 52%, transparent 100%);
}

.bg-orb {
  position: absolute;
  z-index: 2;
  filter: blur(60px);
  opacity: .2;
  transform: translateZ(0);
}

.neo-hero .v-container {
  position: relative;
  z-index: 3;
}

.hero-container {
  padding-left: 5%;
  padding-right: 5%;
  background: transparent;
}

.hero-content-row {
  min-height: clamp(520px, 82vh, 820px);
}

.hero-mobile-video {
  display: none;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 20px;
}

.hero-trusted {
  margin-top: clamp(24px, 4vw, 44px);
  width: 56%;
  min-width: 280px;
}

.hero-trusted-title {
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 6px;
}

.trust-marquee {
  position: relative;
  overflow: hidden;
  mask-image: linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%);
}

.trust-track {
  display: flex;
  align-items: center;
  gap: 14px;
  width: max-content;
  animation: trust-marquee 34s linear infinite;
}

.trust-track.is-paused {
  animation-play-state: paused;
}

.trust-item {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, .84);
  border: 1px solid rgba(15, 23, 42, .08);
  white-space: nowrap;
  font-weight: 700;
  color: #0f172a;
}

.trust-logo-wrap,
.trust-placeholder {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.trust-logo-wrap {
  display: grid;
  place-items: center;
  background: #fff;
  overflow: hidden;
}

.trust-logo {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.trust-placeholder {
  display: grid;
  place-items: center;
  background: linear-gradient(150deg, #dbe8f3, #c8daea);
  font-size: 0.6875rem;
  letter-spacing: .2px;
}

.orb1 {
  width: 420px;
  height: 420px;
  top: -100px;
  left: -80px;
  background: color-mix(in oklab, var(--lf-primary) 50%, white);
}

.orb2 {
  width: 360px;
  height: 360px;
  bottom: -120px;
  right: -60px;
  background: color-mix(in oklab, var(--lf-secondary) 55%, white);
}

.eyebrow {
  font-weight: 500;
  letter-spacing: .2px;
  color: #475569;
  margin-bottom: 8px;
}

.lead {
  font-size: clamp(16px, 1.3vw, 20px);
  color: #475569;
}

.hero-lead {
  max-width: 42ch;
}

.hero-product-heading {
  line-height: 1;
}

.hero-product-logo {
  display: block;
  width: clamp(190px, 31vw, 420px);
  height: auto;
}

.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.badge {
  background: color-mix(in oklab, var(--lf-accent) 25%, white);
}

.badge-new {
  color: #0f172a;
  background: color-mix(in oklab, white 75%, var(--lf-accent));
}

.cta-group {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.cta-primary-lg {
  min-height: 56px;
  padding-inline: 28px;
  font-size: 1rem;
  font-weight: 700;
}

@keyframes trust-marquee {
  0% { transform: translateX(0); }
  100% { transform: translateX(-50%); }
}

@media (max-width: 960px) {
  .neo-hero {
    padding-top: 0;
  }

  .hero-container {
    padding-left: 5%;
    padding-right: 5%;
    background: linear-gradient(180deg, #f8fafc 0%, #edf4fa 62%, #f8fafc 100%);
  }

  .hero-trusted {
    width: 100%;
    min-width: 0;
  }

  .hero-bg-video,
  .hero-video-scrim,
  .bg-orb {
    display: none;
  }

  .hero-content-row {
    min-height: auto;
  }

  .hero-mobile-video {
    display: block;
    width: 100vw;
    max-width: 100vw;
    aspect-ratio: 1 / 1;
    margin-bottom: 0 !important;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    border-radius: 0;
    object-position: center 28%;
    -webkit-mask-image: linear-gradient(to bottom, #000 0%, #000 72%, rgba(0, 0, 0, .7) 86%, transparent 100%);
    mask-image: linear-gradient(to bottom, #000 0%, #000 72%, rgba(0, 0, 0, .7) 86%, transparent 100%);
  }

  .badges {
    gap: 6px;
  }

  .badges :deep(.v-chip) {
    height: 30px;
    font-size: 0.6875rem;
    padding-inline: 10px;
  }

  .badges :deep(.v-chip .v-icon) {
    font-size: 0.875rem;
  }

  .trust-marquee {
    mask-image: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .trust-track {
    animation: none !important;
  }
}
</style>
